<template>
  <section
    class="egb"
    :class="[
      severity.className,
      {
        'egb--compact': compact,
        'egb--empty': !hasGap,
      },
    ]"
    :style="badgeStyle"
    :aria-label="ariaLabel"
  >
    <div class="egb__content">
      <span class="egb__eyebrow">Emotional Gap</span>

      <strong v-if="hasGap" class="egb__value">
        <span class="egb__plus">+</span>{{ normalizedGap }}
        <span class="egb__label">{{ severity.label }}</span>
      </strong>

      <strong v-else class="egb__value egb__value--empty">
        No data
      </strong>

      <p v-if="!compact" class="egb__copy">
        {{ helperCopy }}
      </p>
    </div>

    <div v-if="hasGap && !compact" class="egb__meter" aria-hidden="true">
      <span class="egb__meter-track">
        <span class="egb__meter-fill" :style="{ width: meterWidth }"></span>
      </span>
      <span class="egb__meter-caption">0</span>
      <span class="egb__meter-caption">100</span>
    </div>
  </section>
</template>

<script>
import { getGapSeverity } from '@/utils/coverageHelpers'

export default {
  name: 'EmotionalGapBadge',

  props: {
    gap: {
      type: Number,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    normalizedGap() {
      const value = Number(this.gap)
      return Number.isFinite(value) && value >= 0 ? Math.round(value) : null
    },

    hasGap() {
      return this.normalizedGap !== null
    },

    severity() {
      return getGapSeverity(this.normalizedGap)
    },

    badgeStyle() {
      return {
        '--egb-bg': this.severity.background,
        '--egb-color': this.severity.color,
        '--egb-border': this.severity.color,
      }
    },

    meterWidth() {
      if (!this.hasGap) return '0%'
      return `${Math.min(this.normalizedGap, 100)}%`
    },

    helperCopy() {
      if (!this.hasGap) {
        return 'No emotional gap data is available for this coverage set yet.'
      }

      if (this.normalizedGap >= 56) {
        return 'This story is being framed with sharply different emotional intensity across perspectives.'
      }

      if (this.normalizedGap >= 36) {
        return 'Coverage is showing a meaningful emotional split across perspectives.'
      }

      if (this.normalizedGap >= 16) {
        return 'Coverage differs somewhat in emotional tone, but the split is not extreme.'
      }

      return 'Coverage is relatively aligned in emotional tone across perspectives.'
    },

    ariaLabel() {
      if (!this.hasGap) return 'No emotional gap data'
      return `Emotional gap ${this.normalizedGap}, ${this.severity.label}`
    },
  },
}
</script>

<style scoped>
.egb {
  position: relative;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--egb-border) 38%, transparent);
  border-radius: 22px;
  padding: 18px;
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--egb-color) 16%, transparent), transparent 42%),
    linear-gradient(145deg, #ffffff 0%, var(--egb-bg) 100%);
  color: var(--egb-color);
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.09);
}

.egb::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.egb__content {
  position: relative;
  z-index: 1;
}

.egb__eyebrow {
  display: block;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  line-height: 1;
  text-transform: uppercase;
  opacity: 0.82;
}

.egb__value {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  color: #111827;
  font-size: clamp(2rem, 6vw, 3.15rem);
  font-weight: 950;
  letter-spacing: -0.08em;
  line-height: 0.95;
}

.egb__plus {
  color: var(--egb-color);
  font-size: 0.68em;
  letter-spacing: -0.04em;
}

.egb__label {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.72);
  color: var(--egb-color);
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 1;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--egb-color) 18%, transparent);
}

.egb__value--empty {
  letter-spacing: -0.04em;
}

.egb__copy {
  max-width: 42rem;
  margin: 12px 0 0;
  color: #374151;
  font-size: 0.96rem;
  font-weight: 650;
  line-height: 1.45;
}

.egb__meter {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: auto auto;
  gap: 6px 10px;
  margin-top: 16px;
}

.egb__meter-track {
  grid-column: 1 / -1;
  overflow: hidden;
  height: 9px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.09);
}

.egb__meter-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, color-mix(in srgb, var(--egb-color) 72%, #ffffff), var(--egb-color));
  box-shadow: 0 0 22px color-mix(in srgb, var(--egb-color) 42%, transparent);
}

.egb__meter-caption {
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 800;
}

.egb__meter-caption:last-child {
  text-align: right;
}

.egb--compact {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  border-radius: 999px;
  padding: 9px 12px;
  background: var(--egb-bg);
  box-shadow: none;
}

.egb--compact .egb__content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.egb--compact .egb__eyebrow {
  font-size: 0.66rem;
}

.egb--compact .egb__value {
  margin-top: 0;
  font-size: 0.95rem;
  letter-spacing: -0.03em;
}

.egb--compact .egb__label {
  padding: 4px 7px;
  font-size: 0.67rem;
}

.egb--empty {
  --egb-bg: #f3f4f6;
  --egb-color: #4b5563;
  --egb-border: #9ca3af;
}

@media (max-width: 640px) {
  .egb {
    border-radius: 18px;
    padding: 16px;
  }

  .egb__copy {
    font-size: 0.9rem;
  }

  .egb--compact {
    padding: 8px 10px;
  }
}
</style>
